import { Box, Fade, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SwitchTransition } from 'react-transition-group';
import AutoSizer from 'react-virtualized-auto-sizer';

import { FlowChartProvider } from '@/components/diagrams/FlowChart/context/FlowChartProvider';
import { Loader } from '@/components/progress/Loader/Loader';
import { useDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { useHouseholdDocumentsViewerContext } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.context';
import { EntityDiagramProvider } from '@/modules/entityDiagram/contexts/EntityDiagram.provider';
import { EntityDiagram } from '@/modules/entityDiagram/EntityDiagram';
import {
  DispositionsDiagram_DispositionScenarioFirstDeathFragment,
  DispositionsDiagram_EntityFragment,
  DispositionsDiagram_TestamentaryEntityFragment,
  useDispositionsDiagramClientProfilesQuery,
  useDispositionsDiagramTestamentaryEntitiesQuery,
} from '@/modules/entityDiagram/graphql/DispositionsDiagram.generated';
import { useQueryEntityDiagram } from '@/modules/entityDiagram/hooks/useQueryEntityDiagram';
import { EntityDiagramVariant } from '@/modules/entityDiagram/types';
import { useWaterfallSummaryContext } from '@/modules/estateWaterfall/contexts/waterfallSummary.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { EntityInEstateStatus, EntityKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

/**
 * Single entity disposition diagram rendered inside Dispositive Provisions V2 split-screen.
 */
interface SingleEntityDispositiveDiagramProps {
  initialPrimaryClientId?: string;
}

export function SingleEntityDispositiveDiagram({
  initialPrimaryClientId,
}: SingleEntityDispositiveDiagramProps) {
  const theme = useTheme();
  // Contexts for IDs and primary clients
  const { householdId, primaryClients } = useHouseholdDetailsContext();
  const { targetId } = useHouseholdDocumentsViewerContext();
  // Propagate the waterfall id when in a hypothetical context so the
  // entity query reads hypothetical-scope BDGs (server falls back to live).
  const { waterfall } = useWaterfallSummaryContext();
  const hypotheticalWaterfallId = waterfall?.id ?? null;
  // Subscribe to live dispositions from the form
  const {
    diagramDispositionsFormState,
    recipientDispositions: loadedRecipientDispositions,
    recipientDispositionWarnings,
    nonRootRecipientOverride,
    selectedNode,
    isLoadingRecipientDispositions,
    setBdgDiagramSyncState,
  } = useDispositiveDiagramSyncContext();

  const recipientDispositions = useMemo(() => {
    const warningOnlyDispositions = Object.fromEntries(
      Object.entries(recipientDispositionWarnings).map(
        ([id, hasDispositions]) => [
          id,
          {
            firstDeath: [],
            secondDeath: [],
            hasUnrenderedDispositions: hasDispositions,
          },
        ]
      )
    );
    // Full query/form data takes precedence over warning-only evidence.
    return { ...warningOnlyDispositions, ...loadedRecipientDispositions };
  }, [loadedRecipientDispositions, recipientDispositionWarnings]);

  // Determine the target type based on the ID
  const targetKind = useMemo(() => {
    return targetId ? getPulidKind(targetId) : null;
  }, [targetId]);

  // Internal "who dies first" state. Defaults to the prop, then snaps to
  // the BDG trigger client (account holder / first policy holder) once the
  // entity loads. This is what lets the form-view BDG Switch flip between
  // primary / contingent scenarios without remounting the diagram.
  const [selectedPrimaryClientId, setSelectedPrimaryClientId] = useState(
    initialPrimaryClientId ?? ''
  );

  const entityQuery = useQueryEntityDiagram({
    entityId: targetId ?? '',
    entityDiagramVariant: EntityDiagramVariant.Dispositions,
    selectedPrimaryClientId,
    hypotheticalWaterfallId,
  });

  // Once the entity resolves and we know it's BDG-aware with a default
  // trigger, snap the selection to the trigger client (toggle OFF position)
  // so the diagram doesn't render the alternate scenario on first paint
  // when the household happens to be primaryClients[0] != trigger.
  const {
    bdgDefaultTriggerClientId,
    bdgTriggeringClientIds,
    isBdgEntity,
    bdgIsSurvivorship,
  } = entityQuery;
  // Reset the snap latch when the entity or the parent's initial selection
  // changes so the new entity's trigger client / parent's tab can take
  // effect on the next BDG resolution.
  const didSnapToTriggerRef = useRef(false);
  useEffect(() => {
    didSnapToTriggerRef.current = false;
  }, [targetId, initialPrimaryClientId]);

  // Once the diagram has rendered, transient reloads (e.g. the recipient
  // prefetch firing when a tile click switches the edited target) keep the
  // current diagram on screen rather than dropping back to the full spinner.
  const hasRenderedDiagramRef = useRef(false);
  // Mirror parent-driven changes (e.g., switching the grantor death tab in
  // the DP form) into our internal selection so the diagram tracks the
  // parent's active scenario.
  useEffect(() => {
    if (!initialPrimaryClientId) return;
    setSelectedPrimaryClientId(initialPrimaryClientId);
  }, [initialPrimaryClientId]);
  useEffect(() => {
    if (!isBdgEntity) return;
    if (!bdgDefaultTriggerClientId) return;
    if (didSnapToTriggerRef.current) return;
    didSnapToTriggerRef.current = true;
    if (selectedPrimaryClientId !== bdgDefaultTriggerClientId) {
      setSelectedPrimaryClientId(bdgDefaultTriggerClientId);
    }
  }, [bdgDefaultTriggerClientId, isBdgEntity, selectedPrimaryClientId]);

  // Stable setter we can publish into the sync context for external UI.
  const setSelectedPrimaryClientIdStable = useCallback((id: string) => {
    setSelectedPrimaryClientId(id);
  }, []);

  // Publish BDG metadata so the BDG form sync hook and the out-of-tree
  // death-order Switch can read/mutate the active scenario.
  useEffect(() => {
    if (!isBdgEntity) return;
    if (!selectedPrimaryClientId) return;
    setBdgDiagramSyncState({
      defaultTriggerClientId: bdgDefaultTriggerClientId,
      triggeringClientIds: bdgTriggeringClientIds,
      selectedPrimaryClientId,
      isSurvivorship: bdgIsSurvivorship,
      setSelectedPrimaryClientId: setSelectedPrimaryClientIdStable,
    });
    return () => setBdgDiagramSyncState(null);
  }, [
    bdgDefaultTriggerClientId,
    bdgTriggeringClientIds,
    bdgIsSurvivorship,
    isBdgEntity,
    selectedPrimaryClientId,
    setBdgDiagramSyncState,
    setSelectedPrimaryClientIdStable,
  ]);

  // Query for client profiles
  const { data: clientProfileData, loading: clientProfileLoading } =
    useDispositionsDiagramClientProfilesQuery({
      fetchPolicy: 'no-cache',
      variables: {
        clientProfileWhere: {
          id: targetId ?? '',
        },
        hypotheticalWaterfallId,
        _forceRefetchKey: initialPrimaryClientId,
      },
      skip: targetKind !== PulidKind.ClientProfile,
    });

  // Query for testamentary entities
  const { data: testamentaryEntityData, loading: testamentaryEntityLoading } =
    useDispositionsDiagramTestamentaryEntitiesQuery({
      fetchPolicy: 'no-cache',
      variables: {
        testamentWhere: {
          id: targetId ?? '',
        },
        hypotheticalWaterfallId,
        _forceRefetchKey: initialPrimaryClientId,
      },
      skip: targetKind !== PulidKind.TestamentaryEntity,
    });

  const diagramData = useMemo(() => {
    if (targetKind === PulidKind.Entity) {
      return entityQuery.entity;
    } else if (targetKind === PulidKind.ClientProfile) {
      if (clientProfileLoading) {
        return null;
      }

      const clientProfile = getNodes(clientProfileData?.clientProfiles)[0];
      if (!clientProfile) {
        return null;
      }

      const isPrimaryClient = primaryClients?.some(
        (c) => c.id === clientProfile.id
      );

      // Transform client profile to entity-like structure with actual client name
      const pseudoEntity: DispositionsDiagram_EntityFragment = {
        id: clientProfile.id,
        entityKind: EntityKind.LlcBusinessEntity,
        extendedDisplayKind: '',
        stage: null,
        entityDiagrams: { edges: [] },
        household: { id: householdId ?? '', possiblePrimaryClients: [] }, // Add required household property
        subtype: {
          id: clientProfile.id,
          displayName: clientProfile.displayName,
          inEstateStatus: isPrimaryClient
            ? EntityInEstateStatus.InEstate
            : EntityInEstateStatus.OutOfEstate,
          dispositionScenarios:
            clientProfile.dispositionScenarios as DispositionsDiagram_DispositionScenarioFirstDeathFragment[],
        },
      };

      return pseudoEntity;
    } else if (targetKind === PulidKind.TestamentaryEntity) {
      if (testamentaryEntityLoading) {
        return null;
      }

      const testamentaryEntity:
        | DispositionsDiagram_TestamentaryEntityFragment
        | undefined = getNodes(testamentaryEntityData?.testamentaryEntities)[0];
      if (!testamentaryEntity) {
        return null;
      }

      // Transform testamentary entity to entity-like structure
      const pseudoEntity: DispositionsDiagram_EntityFragment = {
        id: testamentaryEntity.id,
        entityKind: EntityKind.IrrevocableTrust,
        extendedDisplayKind: '',
        stage: null,
        entityDiagrams: { edges: [] },
        household: { id: householdId ?? '', possiblePrimaryClients: [] }, // Add required household property
        subtype: {
          id: testamentaryEntity.id,
          displayName: testamentaryEntity.displayName,
          inEstateStatus: EntityInEstateStatus.OutOfEstate,
          dispositionScenarios:
            testamentaryEntity.dispositionScenarios as DispositionsDiagram_DispositionScenarioFirstDeathFragment[],
        },
      };

      return pseudoEntity;
    }

    return null;
  }, [
    clientProfileData?.clientProfiles,
    clientProfileLoading,
    entityQuery.entity,
    householdId,
    primaryClients,
    targetKind,
    testamentaryEntityData?.testamentaryEntities,
    testamentaryEntityLoading,
  ]);

  const fullLoader = (
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Loader />
    </Box>
  );

  // First paint also waits on the recipient-disposition prefetch so the
  // diagram doesn't pop its second-hop branches in a beat late. Once the
  // diagram has rendered, that prefetch is a background refresh (e.g. when a
  // tile click switches the edited target) and must NOT blank the diagram.
  if (!hasRenderedDiagramRef.current && isLoadingRecipientDispositions) {
    return fullLoader;
  }

  // Hard prerequisites — without these we genuinely have nothing to draw.
  if (
    !householdId ||
    !targetId ||
    !diagramData ||
    !selectedPrimaryClientId ||
    !diagramDispositionsFormState
  ) {
    return fullLoader;
  }

  // Latch so subsequent refreshes keep the current diagram on screen instead
  // of falling back to the spinner above. Safe to write during render: this is
  // a one-way "has rendered once" flag, not derived state.
  hasRenderedDiagramRef.current = true;

  // The keyed Fade remount is the only channel that propagates live form
  // edits into the diagram (the reducer reads dispositions once, at init), so
  // the non-root override must be in the key or its edits never re-render.
  const key = `${targetId}-${selectedPrimaryClientId}-${JSON.stringify(
    diagramDispositionsFormState
  )}-${JSON.stringify(recipientDispositions)}-${JSON.stringify(
    nonRootRecipientOverride
  )}`;

  return (
    <Box position="relative" height="100%" width="100%">
      <AutoSizer>
        {({ height, width }: { height: number; width: number }) => (
          // Cross-fade target swaps: the entering diagram fades in over the
          // outgoing one (both absolutely positioned) rather than the keyed
          // provider unmounting to a blank frame first. Each layer owns its
          // FlowChartProvider, so the exiting snapshot keeps its layout while
          // the new target lays out underneath.
          <SwitchTransition mode="in-out">
            <Fade
              key={key}
              timeout={{
                enter: theme.transitions.duration.short,
                exit: theme.transitions.duration.short,
              }}
              easing={theme.transitions.easing.easeInOut}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                height={height}
                width={width}
              >
                <FlowChartProvider>
                  <EntityDiagramProvider
                    primaryClients={primaryClients ?? []}
                    isTwoClientHousehold={(primaryClients?.length ?? 0) > 1}
                    presentationMode={false}
                    isOnDispositionsFormDiagram={true}
                    isInHalfWidthContainer={true}
                    entity={diagramData}
                    // Use the default layout for the diagram in this case
                    entityDiagram={null}
                    selectedPrimaryClientId={selectedPrimaryClientId}
                    entityDiagramVariant={EntityDiagramVariant.Dispositions}
                    viewOnlyMode={true}
                    externalDispositions={
                      diagramDispositionsFormState ?? undefined
                    }
                    recipientDispositions={recipientDispositions}
                    nonRootRecipientOverride={nonRootRecipientOverride}
                    initialSelectedNode={selectedNode}
                    bdgDefaultTriggerClientId={bdgDefaultTriggerClientId}
                    isBdgEntity={isBdgEntity}
                  >
                    <EntityDiagram
                      height={height}
                      width={width}
                      householdId={householdId}
                      entityId={targetId}
                      hideFullscreenButton
                    />
                  </EntityDiagramProvider>
                </FlowChartProvider>
              </Box>
            </Fade>
          </SwitchTransition>
        )}
      </AutoSizer>
    </Box>
  );
}
